'use client';

import classNames from 'classnames';
import dynamic from 'next/dynamic';
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import NullState from '@alltrails/amplitude/enums/NullState';
import NullStateAction from '@alltrails/amplitude/enums/NullStateAction';
import ReportLocation from '@alltrails/amplitude/enums/ReportLocation';
import ReviewFilterAction from '@alltrails/amplitude/enums/ReviewFilterAction';
import ReviewShowMoreSection from '@alltrails/amplitude/enums/ReviewShowMoreSection';
import useUser from '@alltrails/context/hooks/useUser';
import Typography from '@alltrails/core/components/Typography';
import type { LightboxPhoto, LightboxUser } from '@alltrails/denali/types';
import { SuggestionResult } from '@alltrails/search/types/searchBoxTypes';
import type { Review } from '@alltrails/types/Review';
import type { ReviewPhoto } from '@alltrails/types/ReviewPhoto';
import type { User } from '@alltrails/types/User';
import { useAppDispatch, useAppSelector } from '../../redux';
import { reviewsApi, ReviewsApiResponse, type SearchType, useSearchReviewsQuery } from '../../slices/reviewsApi';
import type { ReviewEntityType } from '../../slices/reviewsSearchFilters';
import type { SortOption } from '../../types/Reviews';
import getReviewFilterCount from '../../utils/getReviewFilterCount';
import { updateSummaryExclusion } from '../../utils/reviewSummaryRequests';
import ReviewsBase from '../ReviewsBase/ReviewsBase';
import ReviewsSort from '../ReviewsSort/ReviewsSort';
import NoResults from '../SearchFilter/NoResults/NoResults';
import ReviewsSearchBox from '../SearchFilter/ReviewsSearchBox';
import ReviewsSearchCount from '../SearchFilter/ReviewsSearchCount/ReviewsSearchCount';
import SearchError from '../SearchFilter/SearchError/SearchError';
import SearchLoading from '../SearchFilter/SearchLoading/SearchLoading';
import isUserDiAdmin from './isUserDiAdmin';
import styles from './SearchFilterReviews.module.scss';

const FilterModal = dynamic(() => import('../SearchFilter/FilterModal'), { ssr: false });

export type ResultsReturnedParams = {
  action?: ReviewFilterAction;
  activityTypes?: string[];
  entityType?: ReviewEntityType;
  months?: number[];
  query?: string;
  ratings?: number[];
};

type SearchFilterReviewsProps = {
  ads?: ReactNode[];
  feedbackKey?: string;
  hideReviewSummary?: boolean;
  initialReviews?: ReviewsApiResponse;
  onActivityLinkClick: (review: Review) => void;
  onDeleteReview?: (review: Review) => void;
  onFilterClick?: () => void;
  onFilterShowMoreClick?: (section: ReviewShowMoreSection) => void;
  onLightboxClose?: () => void;
  onLightboxPhotoViewed?: (review: Review, photo: LightboxPhoto['photo']) => void;
  onLightboxUserClick?: (user: LightboxUser) => void;
  onLoadMore: () => boolean;
  onNullStateCtaClick?: (action: NullStateAction, state: NullState) => void;
  onNullStateViewed?: (state: NullState) => void;
  onResultsReturned?: (params: ResultsReturnedParams) => void;
  onReviewPhotoClick?: (review: Review, photo: ReviewPhoto) => void;
  onReviewPhotosScroll?: (review: Review, numPhotos: number) => void;
  onReviewPhotosScrollEnd?: (review: Review, numPhotos: number) => void;
  onSearchFocus?: () => void;
  onSortChange?: (oldSort: SortOption, newSort: SortOption) => void;
  onToggleSummaryExclusion?: (review: Review, summaryExclusion: boolean) => void;
  onUpdateReview?: (data: { detail: Review }) => void;
  onUserClick?: (user: User) => void;
  openReviewFormModal?: (trailFormProps: any) => void;
  perPage?: number;
  photosClassName?: string;
  reportLocation?: ReportLocation;
  reviewSummary?: string;
  searchBoxClassName?: string;
  searchAndSortClassName?: string;
  sortOption?: SortOption;
  total: number;
  searchType: SearchType;
  photosOverflowOnMobile?: boolean;
  isBot?: boolean;
  helpfulReviews?: Review[];
  showAllInitialReviews?: boolean;
  seoTestLimitReviewsToThreeLonger?: boolean;
  seoTestDisplay5KeywordReviews?: boolean;
  seoKeywordReviews?: Review[];
  showVoteButton?: boolean;
  includeTrailName?: boolean;
  trailLinkNewTab?: boolean;
  skip?: boolean;
  searchSuggestions?: SuggestionResult[];
  sortOptions?: SortOption[];
  totalEmptyState?: ReactNode;
  shouldUseTotalFallback?: boolean;
};

const SearchFilterReviews = ({
  ads,
  initialReviews,
  onActivityLinkClick,
  onDeleteReview,
  onFilterClick,
  onFilterShowMoreClick,
  onLightboxClose,
  onLightboxPhotoViewed,
  onLightboxUserClick,
  onLoadMore,
  onNullStateCtaClick,
  onNullStateViewed,
  onResultsReturned,
  onReviewPhotoClick,
  onReviewPhotosScroll,
  onReviewPhotosScrollEnd,
  onSearchFocus,
  onSortChange,
  onToggleSummaryExclusion,
  onUpdateReview,
  onUserClick,
  openReviewFormModal,
  perPage = 6,
  photosClassName,
  reportLocation,
  searchBoxClassName,
  searchAndSortClassName,
  sortOption = 'atSort',
  total: totalProp,
  searchType,
  photosOverflowOnMobile,
  isBot,
  helpfulReviews,
  showAllInitialReviews,
  seoTestLimitReviewsToThreeLonger,
  seoTestDisplay5KeywordReviews,
  seoKeywordReviews,
  showVoteButton,
  includeTrailName,
  trailLinkNewTab,
  skip,
  searchSuggestions,
  sortOptions = ['atSort', 'desc', 'highestRated', 'lowestRated'],
  totalEmptyState,
  shouldUseTotalFallback
}: SearchFilterReviewsProps) => {
  const currentUser = useUser();
  const containerRef = useRef<HTMLDivElement>(null);
  const reviewsRef = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  const context = useAppSelector(state => state.context);
  const { query, ratings, activities, months } = useAppSelector(state => state.reviewsSearchFilters.permanent);

  const [cursor, setCursor] = useState<string | undefined>();
  const [currentSortOption, setCurrentSortOption] = useState<SortOption>(sortOption);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [renderCachedReviews, setRenderCachedReviews] = useState(false);
  const [searchAction, setSearchAction] = useState<ReviewFilterAction | undefined>();

  const [loadMoreCalledOnce, setLoadMoreCalledOnce] = useState(false);

  const onSortChangeInternal = useCallback(
    (oldSort: SortOption, newSort: SortOption) => {
      setCurrentSortOption(newSort);
      onSortChange?.(oldSort, newSort);
    },
    [onSortChange]
  );

  const { data, isFetching, isError } = useSearchReviewsQuery(
    {
      after: cursor,
      limit: perPage,
      rating: ratings,
      activity: activities,
      month: months,
      query,
      sortOption: currentSortOption,
      ...searchType
    },
    { skip: !renderCachedReviews || !!skip }
  );
  const total = shouldUseTotalFallback ? totalProp || data?.exclusiveMaximum || data?.meta?.items || 0 : totalProp;

  const onToggleSummaryExclusionInternal = useCallback(
    (review: Review, summaryExclusion: boolean) => {
      onToggleSummaryExclusion?.(review, summaryExclusion);
      updateSummaryExclusion(review, summaryExclusion).catch((e: Error) => {
        console.error(`Failed to update summary exclusion: ${e?.message}`);
      });
    },
    [onToggleSummaryExclusion]
  );

  const onFilterClickInternal = useCallback(() => {
    onFilterClick?.();
    setFilterModalOpen(true);
  }, [onFilterClick]);

  const resetPage = useCallback(() => setCursor(undefined), []);

  const onSearch = useCallback((action?: ReviewFilterAction) => {
    resetPage();
    setSearchAction(action);
    setTimeout(() => {
      containerRef.current?.focus({ preventScroll: true });
      containerRef.current?.scrollIntoView();
    }, 0);
  }, []);

  const onNullStateCtaClickInternal = useCallback(
    (action: NullStateAction, state: NullState) => {
      onNullStateCtaClick?.(action, state);
      containerRef.current?.focus({ preventScroll: true });
      containerRef.current?.scrollIntoView();
    },
    [onNullStateCtaClick]
  );

  const onNoResultsClear = useCallback(
    (action: NullStateAction, state: NullState) => {
      onNullStateCtaClickInternal?.(action, state);
      resetPage();
    },
    [onNullStateCtaClickInternal]
  );

  const onLoadMoreInternal = useCallback(() => {
    if (!loadMoreCalledOnce && seoTestLimitReviewsToThreeLonger) {
      setLoadMoreCalledOnce(true);
      return;
    }
    if (onLoadMore()) {
      setCursor(data?.pageInfo?.nextCursor);
      setLoadMoreCalledOnce(true);
    }
  }, [data?.pageInfo?.nextCursor, onLoadMore, loadMoreCalledOnce, seoTestLimitReviewsToThreeLonger]);

  useEffect(() => {
    if (!data?.trail_reviews.length || isFetching || isError || !searchAction) return;
    onResultsReturned?.({
      action: searchAction,
      activityTypes: activities,
      entityType: 'reviews',
      months,
      query,
      ratings
    });
  }, [data?.trail_reviews, isError, isFetching, searchAction]);

  /**
   * HACK: Initialize the reviews cache with the initial set of reviews passed from props.
   * See https://redux-toolkit.js.org/rtk-query/usage/server-side-rendering for ideal SSR setup.
   * With the introduction of RSC's however, there is no formal way to initialize the cache inside server components.
   */
  const initializeReviewsCache = async () => {
    if (initialReviews) {
      await dispatch(
        reviewsApi.util.upsertQueryData(
          'searchReviews',
          {
            after: cursor,
            limit: perPage,
            rating: ratings,
            activity: activities,
            month: months,
            query,
            sortOption: currentSortOption,
            ...searchType
          },
          initialReviews
        )
      );
    }

    setRenderCachedReviews(true);
  };

  useEffect(() => {
    if (initialReviews?.trail_reviews.length) {
      initializeReviewsCache();
    } else {
      setRenderCachedReviews(true);
    }
    // only run this once to avoid unwanted manual cache updates
  }, []);

  const reviews = data?.trail_reviews ? data.trail_reviews : (initialReviews?.trail_reviews ?? []);

  if (total === 0 && !totalEmptyState) {
    return <></>;
  }

  const hasFilters = getReviewFilterCount('reviews', ratings, activities, months) > 0;
  const hasSearch = !!query?.length;
  const canLoadMore = data?.pageInfo?.hasNextPage ?? !!(data?.trail_reviews?.length && data.trail_reviews.length);
  const renderCount = data?.exclusiveMaximum ?? data?.meta.items ?? data?.pageInfo?.itemCount ?? data?.trail_reviews?.length ?? perPage;

  const isLoggedIn = !!currentUser;

  let displayReviews = reviews;
  if (helpfulReviews?.length && !isUserDiAdmin(currentUser?.id) && !hasFilters && !hasSearch) {
    const helpfulReviewsIds = helpfulReviews.map(review => review.id);
    const otherReviews = reviews.filter(review => !helpfulReviewsIds.includes(review.id));
    displayReviews = helpfulReviews.concat(otherReviews);
  }

  if (isLoggedIn) {
    displayReviews = loadMoreCalledOnce || showAllInitialReviews ? displayReviews : displayReviews.slice(0, 5);
  }

  if (!isLoggedIn || isBot) {
    if (seoTestLimitReviewsToThreeLonger) {
      displayReviews = loadMoreCalledOnce ? displayReviews : displayReviews.slice(0, 3);
    }
    if (!isBot && !seoTestLimitReviewsToThreeLonger) {
      displayReviews = loadMoreCalledOnce ? displayReviews : displayReviews.slice(0, 5);
    }
    if (seoTestDisplay5KeywordReviews) {
      const seoReviewIds = seoKeywordReviews?.map(review => review.id) ?? [];
      const fillerReviews = (displayReviews ?? []).filter(review => !seoReviewIds.includes(review.id));
      displayReviews = [...(seoKeywordReviews ?? []), ...fillerReviews].slice(0, 5);
    }
  }

  const handleSortChange = (newSort: SortOption) => {
    onSortChangeInternal(currentSortOption, newSort);
  };

  return (
    <div ref={containerRef} className={styles.container} tabIndex={-1}>
      <div className={classNames(styles.searchAndSort, searchAndSortClassName)}>
        <ReviewsSearchBox
          className={searchBoxClassName}
          onFilterClick={onFilterClickInternal}
          onSearch={onSearch}
          onSearchFocus={onSearchFocus}
          searchSuggestions={searchSuggestions}
        />
        <ReviewsSort sortOption={currentSortOption} sortOptions={sortOptions} onSortChanged={handleSortChange} />
      </div>
      {!isError && (!(isFetching && hasSearch) || !!cursor) && (hasFilters || hasSearch) && renderCount > 0 && (
        <ReviewsSearchCount count={renderCount} hasFilters={hasFilters} hasSearch={hasSearch} query={query} />
      )}
      {isFetching && !cursor && <SearchLoading repeatNum={6} />}
      {isError && <SearchError onCtaClick={onNullStateCtaClickInternal} onViewed={onNullStateViewed} searchType={searchType} />}
      {(!isFetching || !!cursor) && !isError && (
        <ReviewsBase
          ref={reviewsRef}
          ads={ads}
          canLoadMore={canLoadMore}
          context={context}
          emptyState={
            <>
              {total === 0 && totalEmptyState && <div className={styles.emptyReviews}>{totalEmptyState}</div>}
              {total !== 0 && <NoResults hasFilters={hasFilters} hasSearch={hasSearch} onClear={onNoResultsClear} onViewed={onNullStateViewed} />}
            </>
          }
          isLoadingMore={isFetching && !!cursor}
          localizeOnLoad
          onActivityLinkClick={onActivityLinkClick}
          onDeleteReview={onDeleteReview}
          onLightboxClose={onLightboxClose}
          onLightboxPhotoViewed={onLightboxPhotoViewed}
          onLightboxUserClick={onLightboxUserClick}
          onLoadMore={onLoadMoreInternal}
          onReviewPhotoClick={onReviewPhotoClick}
          onReviewPhotosScroll={onReviewPhotosScroll}
          onReviewPhotosScrollEnd={onReviewPhotosScrollEnd}
          onToggleSummaryExclusion={onToggleSummaryExclusionInternal}
          onUpdateReview={onUpdateReview}
          onUserClick={onUserClick}
          openReviewFormModal={openReviewFormModal}
          preferHTML
          photosClassName={photosClassName}
          reportLocation={reportLocation}
          resultsFooter={
            total !== 0 &&
            !isError &&
            (hasFilters || hasSearch) &&
            renderCount > 0 && (
              <Typography className={styles.localeMessaging} color="secondary" component="div" variant="text100">
                <FormattedMessage defaultMessage="Search results don’t include reviews that have been translated from another language." />
              </Typography>
            )
          }
          reviews={displayReviews}
          total={total}
          photosOverflowOnMobile={photosOverflowOnMobile}
          showVoteButton={showVoteButton}
          includeTrailName={includeTrailName}
          trailLinkNewTab={trailLinkNewTab}
        />
      )}
      {filterModalOpen && (
        <FilterModal
          initialReviewsCount={total}
          isOpen={filterModalOpen}
          onClose={() => setFilterModalOpen(false)}
          onFilterSave={onSearch}
          onShowMoreClick={onFilterShowMoreClick}
          perPage={perPage}
          searchType={searchType}
        />
      )}
    </div>
  );
};
export default SearchFilterReviews;
